import React from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket } from 'lucide-react';
import { Button, Spinner } from '@hero/web-components';
import { buttonsColors } from '@hero/web-theme';
import { cn } from '@hero/web-lib';

export interface OrderCreateButtonProps {
  /** Loading state */
  isLoading?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Button text when not loading */
  text?: string;
  /** Button text when loading */
  loadingText?: string;
  /** Button type */
  type?: 'button' | 'submit' | 'reset';
  /** Optional className to override or extend default styles */
  className?: string;
  /** Full width button (default: true) */
  fullWidth?: boolean;
  /** Show Rocket icon (default: true) */
  showIcon?: boolean;
  /**
   * When true, the button shows only the icon and expands to reveal
   * the text on hover with a smooth animation. (default: false)
   */
  enableDynamicExpanding?: boolean;
}

export function OrderCreateButton({
  isLoading = false,
  disabled = false,
  onClick,
  text,
  loadingText,
  type = 'button',
  className,
  fullWidth = true,
  showIcon = true,
  enableDynamicExpanding = false,
}: OrderCreateButtonProps) {
  const { t } = useTranslation('m-order');

  const defaultText = text ?? t('create_button.create');
  const defaultLoadingText = loadingText ?? t('create_button.creating');

  if (enableDynamicExpanding) {
    return (
      <Button
        type={type}
        className={cn(
          buttonsColors.superButtonStyle,
          'group/expand overflow-hidden rounded-full transition-all duration-300 ease-in-out p-0',
          'h-10 w-10 min-w-10 hover:w-48 hover:px-5',
          className,
        )}
        disabled={disabled || isLoading}
        onClick={onClick}>
        <span className={buttonsColors.superButtonShine} />
        <span className="relative z-10 flex items-center justify-center whitespace-nowrap">
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              <Rocket className="!size-5 shrink-0" />
              <span className="max-w-0 overflow-hidden opacity-0 transition-all duration-300 ease-in-out group-hover/expand:max-w-[200px] group-hover/expand:opacity-100 group-hover/expand:ms-2">
                {defaultText}
              </span>
            </>
          )}
        </span>
      </Button>
    );
  }

  return (
    <Button
      type={type}
      className={cn(buttonsColors.superButtonStyle, 'h-12', fullWidth && 'w-full', className)}
      disabled={disabled || isLoading}
      onClick={onClick}>
      <span className={buttonsColors.superButtonShine} />
      <span className="relative z-10 flex items-center gap-2">
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            {defaultText}
            {showIcon && <Rocket className="!size-5" />}
          </>
        )}
      </span>
    </Button>
  );
}
